import React from "react";
import { motion } from "framer-motion";
import { Quote, Search, Download, Share2, Loader2, ScrollText, ChevronDown, ChevronUp } from "lucide-react";
import { toPng } from "html-to-image";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import { toDataUrl } from "../lib/images";
import ReelDownloadButton from "../components/site/ReelDownloadButton";
import Newsletter from "../components/site/Newsletter";
import FullImage from "../components/site/FullImage";

export default function Proverbs() {
  useSEO(
    "Yorùbá Proverbs (Òwe) — Wisdom of the Elders",
    "Explore a growing collection of Yorùbá proverbs (òwe) and their deeper meanings. Discover the wit, ethics, and wisdom woven into Yorùbá thought."
  );

  const [proverbs, setProverbs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [term, setTerm] = React.useState("");
  const [theme, setTheme] = React.useState("all");

  const [shareTarget, setShareTarget] = React.useState(null);
  const [cardImg, setCardImg] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const [expanded, setExpanded] = React.useState({});
  const cardRef = React.useRef(null);
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  React.useEffect(() => {
    api
      .get("/proverbs")
      .then((r) => setProverbs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const themes = React.useMemo(() => {
    const set = new Set(proverbs.map((p) => p.theme).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [proverbs]);

  const fold = (s) =>
    (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const filtered = proverbs.filter((p) => {
    if (theme !== "all" && p.theme !== theme) return false;
    if (term.trim()) {
      const q = fold(term.trim());
      return fold(p.yoruba).includes(q) || fold(p.meaning).includes(q) || fold(p.theme).includes(q);
    }
    return true;
  });

  const generatePng = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true, backgroundColor: "#12100e" });
  };

  // shareTarget is applied to the hidden card via state; run capture after paint.
  const withCard = async (proverb, action) => {
    setShareTarget(proverb);
    setBusy(`${action}-${proverb.id}`);
    setCardImg(proverb.image ? await toDataUrl(proverb.image) : null);
    await new Promise((res) => setTimeout(res, 120));
    try {
      const url = await generatePng();
      if (!url) return;
      if (action === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = `yoruba-proverb.png`;
        a.click();
      } else {
        const blob = await (await fetch(url)).blob();
        const file = new File([blob], "yoruba-proverb.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "A Yorùbá Proverb",
            text: `${proverb.yoruba} — via ÀsÀNigHT Worldwide`,
          });
        } else {
          const a = document.createElement("a");
          a.href = url;
          a.download = `yoruba-proverb.png`;
          a.click();
        }
      }
    } catch (e) {
      console.warn("Proverb share card could not be generated or was cancelled:", e);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-night text-cream min-h-screen">
      {/* HERO */}
      <header className="max-w-5xl mx-auto px-6 pt-24 pb-10">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <ScrollText className="w-4 h-4 text-gold" />
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Yorùbá Proverbs · Òwe</p>
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="proverbs-title">
            The Wisdom of the Elders
          </h1>
          <p className="text-cream/70 text-lg leading-relaxed max-w-2xl">
            “Òwe l'ẹṣin ọ̀rọ̀” — a proverb is the horse that carries meaning. Explore Yorùbá
            proverbs and the deeper wisdom woven into each one.
          </p>
          <div className="mt-6">
            <ReelDownloadButton section="proverbs" label="Download Reel (MP4)" />
          </div>
        </Reveal>
      </header>

      {/* CONTROLS */}
      <div className="max-w-5xl mx-auto px-6 pb-8 space-y-5">
        <Reveal>
          <div className="relative">
            <Search className="w-4 h-4 text-cream/40 absolute left-5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search proverbs, meanings, or themes…"
              data-testid="proverbs-search-input"
              className="w-full rounded-full bg-night-surface border border-cream/20 pl-12 pr-6 py-3.5 text-cream placeholder:text-cream/40 focus:outline-none focus:border-gold transition-colors"
            />
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="flex flex-wrap gap-2">
            {themes.map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                data-testid={`proverbs-theme-${t}`}
                className={`text-sm rounded-full px-5 py-2 border transition-colors ${
                  theme === t
                    ? "bg-gold text-night border-gold font-semibold"
                    : "border-cream/20 text-cream/75 hover:border-gold hover:text-gold"
                }`}
              >
                {t === "all" ? "All Themes" : t}
              </button>
            ))}
            <span className="ml-auto self-center text-sm text-cream/45" data-testid="proverbs-count">
              {filtered.length} {filtered.length === 1 ? "proverb" : "proverbs"}
            </span>
          </div>
        </Reveal>
      </div>

      {/* LIST */}
      <div className="max-w-5xl mx-auto px-6 pb-24">
        {loading ? (
          <p className="text-cream/50 py-16 text-center">Loading proverbs…</p>
        ) : filtered.length === 0 ? (
          <p className="text-cream/50 py-16 text-center" data-testid="proverbs-empty">
            No proverbs match your search yet.
          </p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 0.05}>
                <div
                  className="h-full flex flex-col border border-cream/12 rounded-2xl bg-night-surface overflow-hidden"
                  data-testid={`proverb-card-${p.id}`}
                >
                  {p.image && (
                    <FullImage src={p.image} alt={p.theme || "proverb"} className="h-44" />
                  )}
                  <div className="flex flex-col flex-1 p-7">
                  <div className="flex items-center justify-between mb-4">
                    <Quote className="w-6 h-6 text-gold/50" />
                    {p.theme && (
                      <span className="text-[10px] uppercase tracking-widest border border-cream/25 rounded-full px-2.5 py-0.5 text-cream/55">
                        {p.theme}
                      </span>
                    )}
                  </div>
                  <p className="font-serif text-xl text-gold leading-snug mb-3">{p.yoruba}</p>
                  {p.translation && (
                    <p className="text-cream/55 text-sm italic leading-relaxed mb-3">“{p.translation}”</p>
                  )}
                  {p.meaning && (
                    <p className="text-cream/70 text-[15px] leading-relaxed mb-4">{p.meaning}</p>
                  )}
                  {(p.explanation || p.usage) && (
                    <>
                      {expanded[p.id] && (
                        <>
                          {p.explanation && (
                            <div className="mb-4">
                              <p className="text-[10px] uppercase tracking-widest text-gold-soft mb-1">The Wisdom</p>
                              <p className="text-cream/70 text-[15px] leading-relaxed whitespace-pre-line">{p.explanation}</p>
                            </div>
                          )}
                          {p.usage && (
                            <div className="mb-4">
                              <p className="text-[10px] uppercase tracking-widest text-gold-soft mb-1">When It's Said</p>
                              <p className="text-cream/70 text-[15px] leading-relaxed whitespace-pre-line">{p.usage}</p>
                            </div>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => toggleExpand(p.id)}
                        data-testid={`proverb-toggle-${p.id}`}
                        className="self-start inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-gold hover:text-gold-light transition-colors mb-5"
                      >
                        {expanded[p.id] ? (
                          <>Read less <ChevronUp className="w-3.5 h-3.5" /></>
                        ) : (
                          <>Read more <ChevronDown className="w-3.5 h-3.5" /></>
                        )}
                      </button>
                    </>
                  )}
                  <div className="flex flex-wrap gap-2 mt-auto pt-2">
                    <button
                      onClick={() => withCard(p, "download")}
                      disabled={busy !== ""}
                      data-testid={`proverb-download-${p.id}`}
                      className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-4 py-2 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50"
                    >
                      {busy === `download-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      Card
                    </button>
                    <button
                      onClick={() => withCard(p, "share")}
                      disabled={busy !== ""}
                      data-testid={`proverb-share-${p.id}`}
                      className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm font-semibold px-4 py-2 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
                    >
                      {busy === `share-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                      Share
                    </button>
                  </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {/* Hidden branded share card */}
      {shareTarget && (
        <div aria-hidden="true" style={{ position: "fixed", top: 0, left: "-20000px", pointerEvents: "none" }}>
          <div
            ref={cardRef}
            data-testid="proverb-share-card"
            style={{
              width: 1080,
              height: 1080,
              background: "linear-gradient(155deg, #1b3327 0%, #12100e 55%)",
              color: "#F5EFE6",
              padding: 90,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", inset: 40, border: "2px solid rgba(212,169,74,0.35)", borderRadius: 28, pointerEvents: "none" }} />

            <div style={{ position: "relative" }}>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 46, color: "#D4A94A", lineHeight: 1 }}>
                ÀsÀNigHT
              </div>
              <div style={{ fontSize: 16, letterSpacing: 8, color: "rgba(245,239,230,0.6)", marginTop: 6 }}>
                WORLDWIDE
              </div>
              <div style={{ fontSize: 20, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginTop: 40 }}>
                Yorùbá Proverb · Òwe
              </div>
            </div>

            <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
              {cardImg && (
                <img
                  src={cardImg}
                  alt=""
                  style={{ width: "100%", height: 300, objectFit: "cover", borderRadius: 20, border: "3px solid rgba(212,169,74,0.55)", marginBottom: 34 }}
                />
              )}
              <div style={{ fontSize: 120, color: "rgba(212,169,74,0.35)", fontFamily: "Georgia, serif", lineHeight: 0.6, marginBottom: 10 }}>“</div>
              <div
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  color: "#D4A94A",
                  fontSize: shareTarget.yoruba.length > 120 ? 44 : shareTarget.yoruba.length > 70 ? 56 : 68,
                  lineHeight: 1.15,
                  marginBottom: 28,
                }}
              >
                {shareTarget.yoruba}
              </div>
              {shareTarget.meaning && (
                <div style={{ fontSize: 32, lineHeight: 1.4, color: "#F5EFE6" }}>
                  {shareTarget.meaning}
                </div>
              )}
            </div>

            <div style={{ position: "relative", borderTop: "1px solid rgba(245,239,230,0.15)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 26, color: "#D4A94A", letterSpacing: 1 }}>asanightworldwide.com</div>
              {shareTarget.theme && (
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: 24, color: "rgba(245,239,230,0.5)" }}>
                  {shareTarget.theme}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Newsletter />
    </div>
  );
}
